import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BookOpen,
  Flag,
  GraduationCap,
  HeartPulse,
  Palmtree,
  PinOff,
  School,
  TrendingUp,
} from 'lucide-react';
import {
  FinancialPlanAccounts,
  GrowthTimelineResult,
  TimelineMilestoneKind,
  TimelineYearPoint,
} from '../../types/financialPlan';
import { formatCurrency } from '../../utils/lifetimeGoalsCalc';
import { incomePhaseLabelAtAge } from '../../utils/financialPlanDefaults';
import {
  buildRequiredIncomeData,
  calendarYearFromPlanTimeline,
  RequiredIncomePoint,
} from '../../utils/lgcRequiredIncomeSeries';
import {
  ACCOUNT_CHART_COLORS,
  ACCOUNT_CHART_LABELS,
  ACCOUNT_KEYS,
} from '../../utils/lifetimeGoalsTimeline';
import './LgcGrowthTimeline.css';
import './LgcTimelineHero.css';

type AccountKey = keyof FinancialPlanAccounts;

const PLAN_AREA_KEYS: { key: AccountKey; dataKey: keyof TimelineYearPoint }[] = [
  { key: 'k401', dataKey: 'onTrackK401' },
  { key: 'rothIra', dataKey: 'onTrackRothIra' },
  { key: 'hsa', dataKey: 'onTrackHsa' },
  { key: 'taxable', dataKey: 'onTrackTaxable' },
];

function planAccountBalance(point: TimelineYearPoint, key: AccountKey): number {
  const entry = PLAN_AREA_KEYS.find((item) => item.key === key);
  return entry ? (point[entry.dataKey] as number) : 0;
}

function GrowthPlanTooltip({
  active,
  payload,
  timeline,
  hiddenAccounts,
  coordinate,
  viewBox,
}: {
  active?: boolean;
  payload?: Array<{ payload?: TimelineYearPoint }>;
  timeline: GrowthTimelineResult;
  hiddenAccounts: Set<AccountKey>;
  coordinate?: { x: number; y: number };
  viewBox?: { width?: number; height?: number };
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  const calendarYear = calendarYearFromPlanTimeline(timeline, point.age);
  const accountRows = ACCOUNT_KEYS.filter((key) => !hiddenAccounts.has(key)).map((key) => ({
    key,
    label: ACCOUNT_CHART_LABELS[key],
    color: ACCOUNT_CHART_COLORS[key],
    value: planAccountBalance(point, key),
  }));

  return (
    <div
      className="lgc-growth-plan-tooltip"
      role="tooltip"
      style={chartTooltipFlipStyle(coordinate, viewBox)}
    >
      <p className="lgc-growth-plan-tooltip__head">
        Age {point.age} · {calendarYear}
        {point.phase === 'drawdown' && (
          <span className="lgc-growth-plan-tooltip__phase"> · Drawdown</span>
        )}
      </p>
      <table className="lgc-growth-plan-tooltip__table">
        <thead>
          <tr>
            <th scope="col">Account</th>
            <th scope="col">Estimated</th>
          </tr>
        </thead>
        <tbody>
          {accountRows.map((row) => (
            <tr key={row.key}>
              <td>
                <span
                  className="lgc-growth-plan-tooltip__swatch"
                  style={{ backgroundColor: row.color }}
                  aria-hidden
                />
                {row.label}
              </td>
              <td className="lgc-mono">{formatCurrency(row.value)}</td>
            </tr>
          ))}
          <tr className="lgc-growth-plan-tooltip__row--total">
            <td>Total planned</td>
            <td className="lgc-mono">{formatCurrency(point.totalOnTrack)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const MILESTONE_COLORS: Record<string, string> = {
  college: '#3B82F6',
  medicare: '#DC2626',
  retirement: '#2C3E2F',
  planEnd: '#94A3B8',
  k12Start: '#6366F1',
  k12End: '#6366F1',
};

type RequiredIncomeView = 'growth' | 'required';

type RequiredCategoryKey =
  | 'budget'
  | 'debt'
  | 'k12'
  | 'saveRetirement'
  | 'saveOther'
  | 'tax'
  | 'retireSep'
  | 'retireRoth'
  | 'retireHsa'
  | 'retireTaxable'
  | 'surplusOpen';

const RETIRE_ALLOC_CHART_KEYS: Array<{
  key: 'retireSep' | 'retireRoth' | 'retireHsa' | 'retireTaxable';
  accountKey: AccountKey;
}> = [
  { key: 'retireSep', accountKey: 'k401' },
  { key: 'retireRoth', accountKey: 'rothIra' },
  { key: 'retireHsa', accountKey: 'hsa' },
  { key: 'retireTaxable', accountKey: 'taxable' },
];

const REQUIRED_CATEGORIES: Array<{
  key: RequiredCategoryKey;
  label: string;
  color: string;
  /** Shown above required gross — surplus allocation layers */
  allocation?: boolean;
  /** Expanded description for tooltip / legend title */
  hint?: string;
}> = [
  { key: 'budget', label: 'Budget', color: '#6B6960' },
  { key: 'debt', label: 'Debt', color: '#78716C' },
  { key: 'k12', label: 'K-12', color: '#6366F1', hint: 'Private-school tuition paid from cash flow' },
  { key: 'saveRetirement', label: 'Save · retirement', color: '#1a472a' },
  {
    key: 'saveOther',
    label: 'Save · other goals',
    color: '#4a7c59',
    hint: '529/college savings, K-12 savings, health reserve & lifestyle goals',
  },
  { key: 'tax', label: 'Tax', color: '#94A3B8', hint: 'Income tax on required gross (effective rate from Profile)' },
  ...RETIRE_ALLOC_CHART_KEYS.map(({ key, accountKey }) => ({
    key,
    label: ACCOUNT_CHART_LABELS[accountKey],
    color: ACCOUNT_CHART_COLORS[accountKey],
    allocation: true as const,
    hint: 'Surplus allocated to this account on the goal path',
  })),
  {
    key: 'surplusOpen',
    label: 'Surplus (open)',
    color: '#99f6e4',
    allocation: true,
    hint: 'Take-home surplus not routed to retirement accounts',
  },
];

const REQUIRED_INCOME_LINE_COLOR = '#0F172A';

const STRIP_MARKER_COLORS: Record<string, string> = {
  ...MILESTONE_COLORS,
  incomePhase: '#4338ca',
};

type StripMarkerKind = TimelineMilestoneKind | 'incomePhase';

type StripMarker = {
  id: string;
  kind: StripMarkerKind;
  label: string;
};

function buildStripMarkerMap(timeline: GrowthTimelineResult): Map<number, StripMarker[]> {
  const byAge = new Map<number, StripMarker[]>();
  const add = (age: number, marker: StripMarker) => {
    if (age < timeline.currentAge || age > timeline.endAge) return;
    const list = byAge.get(age) ?? [];
    if (list.some((m) => m.id === marker.id)) return;
    list.push(marker);
    byAge.set(age, list);
  };

  timeline.milestones.forEach((m) => {
    add(m.age, { id: m.id, kind: m.kind, label: m.label });
  });

  const sortedPhases = [...timeline.incomePhases].sort((a, b) => a.startAge - b.startAge);
  sortedPhases.forEach((phase, index) => {
    const trimmed = phase.label?.trim();
    const phaseName = trimmed || `Phase ${index + 1}`;
    add(phase.startAge, {
      id: `income-start-${phase.id}`,
      kind: 'incomePhase',
      label: `${phaseName} income starts`,
    });
  });

  return byAge;
}

function StripMarkerIcon({ kind, size = 11 }: { kind: StripMarkerKind; size?: number }) {
  const iconProps = { size, strokeWidth: 2, 'aria-hidden': true as const };
  switch (kind) {
    case 'college':
      return <GraduationCap {...iconProps} />;
    case 'k12Start':
      return <School {...iconProps} />;
    case 'k12End':
      return <BookOpen {...iconProps} />;
    case 'medicare':
      return <HeartPulse {...iconProps} />;
    case 'retirement':
      return <Palmtree {...iconProps} />;
    case 'planEnd':
      return <Flag {...iconProps} />;
    case 'incomePhase':
      return <TrendingUp {...iconProps} />;
    default:
      return null;
  }
}

interface LgcTimelineHeroProps {
  timeline: GrowthTimelineResult;
  /** When true, fills the main content area. */
  fullPage?: boolean;
  /** Pinning an age opens the matching Edit Plan group. */
  onPinEdit?: (age: number) => void;
}

function formatAxisMoney(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

const CHART_TOOLTIP_WRAPPER_STYLE: React.CSSProperties = {
  zIndex: 1200,
  pointerEvents: 'none',
  outline: 'none',
};

function chartTooltipFlipStyle(
  coordinate?: { x: number; y: number },
  viewBox?: { width?: number; height?: number },
): React.CSSProperties | undefined {
  if (!coordinate || !viewBox?.height) return undefined;
  if (coordinate.y > viewBox.height * 0.52) {
    return { transform: 'translateY(calc(-100% - 16px))' };
  }
  return undefined;
}

function milestoneChartLabel(m: GrowthTimelineResult['milestones'][0], full = false): string {
  if (full) return m.label;
  const firstName = m.label.split(' ')[0] ?? '';
  if (m.kind === 'college') return firstName.slice(0, 8);
  if (m.kind === 'medicare') return 'Medicare';
  if (m.kind === 'k12Start') return `${firstName} · K-12`;
  if (m.kind === 'k12End') return '';
  if (m.kind === 'retirement') return 'Retire';
  if (m.kind === 'planEnd') return 'End';
  return m.label.slice(0, 10);
}

/** Milestones drawn on the chart — omit K-12 end markers. */
function chartMilestones(milestones: GrowthTimelineResult['milestones']) {
  return milestones.filter((m) => m.kind !== 'k12End');
}

function milestonesAtAge(milestones: GrowthTimelineResult['milestones'], age: number) {
  return milestones.filter((m) => m.age === age);
}

function groupMilestonesByAge(milestones: GrowthTimelineResult['milestones']) {
  const byAge = new Map<number, GrowthTimelineResult['milestones']>();
  milestones.forEach((m) => {
    const list = byAge.get(m.age) ?? [];
    list.push(m);
    byAge.set(m.age, list);
  });
  return Array.from(byAge.entries()).sort(([a], [b]) => a - b);
}

/** Max stacked milestone labels at any single age — used for stable chart top margin. */
function maxMilestoneLabelCount(milestones: GrowthTimelineResult['milestones']): number {
  const grouped = groupMilestonesByAge(chartMilestones(milestones));
  if (grouped.length === 0) return 0;
  return Math.max(
    ...grouped.map(([, items]) =>
      items.filter((m) => milestoneChartLabel(m, true).length > 0).length,
    ),
  );
}

const MILESTONE_LABEL_LINE_HEIGHT = 12;
const CHART_TOP_MARGIN_BASE = 14;
/** Must match XAxis `height` — tickMargin + 10px labels + small bottom pad. */
const HERO_X_AXIS_HEIGHT = 15;

function chartTopMarginForTimeline(milestones: GrowthTimelineResult['milestones']): number {
  const labelCount = maxMilestoneLabelCount(milestones);
  if (labelCount <= 0) return 22;
  return CHART_TOP_MARGIN_BASE + labelCount * MILESTONE_LABEL_LINE_HEIGHT;
}

function milestoneLabelAnchor(age: number, domainStart: number, domainEnd: number): 'start' | 'middle' | 'end' {
  const span = Math.max(domainEnd - domainStart, 1);
  const edge = span * 0.05;
  if (age - domainStart <= edge) return 'start';
  if (domainEnd - age <= edge) return 'end';
  return 'middle';
}

function MilestoneChartLabel({
  viewBox,
  x: xProp,
  y: yProp,
  items,
  textAnchor = 'middle',
  fullLabel = true,
}: {
  viewBox?: { x?: number; y?: number };
  x?: number;
  y?: number;
  items: GrowthTimelineResult['milestones'];
  textAnchor?: 'start' | 'middle' | 'end';
  fullLabel?: boolean;
}) {
  const x = viewBox?.x ?? xProp ?? 0;
  const y = viewBox?.y ?? yProp ?? 0;
  const visibleItems = items
    .map((m) => ({ m, label: milestoneChartLabel(m, fullLabel) }))
    .filter((entry) => entry.label.length > 0);
  if (visibleItems.length === 0) return null;

  const lineHeight = MILESTONE_LABEL_LINE_HEIGHT;
  const labelStartY = y - 8 - (visibleItems.length - 1) * lineHeight;
  const fontSize = 8;
  const padX = 3;
  const padY = 2;
  const charW = 4.5;

  return (
    <g aria-hidden>
      {visibleItems.map(({ m, label }, i) => {
        const color = MILESTONE_COLORS[m.kind] ?? '#475569';
        const textY = labelStartY + i * lineHeight;
        const anchorOffset =
          textAnchor === 'start' ? 0 : textAnchor === 'end' ? -(label.length * charW + padX * 2) : -(label.length * charW) / 2 - padX;
        const rectX = x + anchorOffset;
        const rectW = label.length * charW + padX * 2;

        return (
          <g key={m.id}>
            <rect
              x={rectX}
              y={textY - fontSize - padY + 1}
              width={rectW}
              height={fontSize + padY * 2}
              rx={3}
              fill="#fff"
              fillOpacity={0.92}
              stroke={color}
              strokeOpacity={0.3}
              strokeWidth={0.75}
            />
            <text
              x={x}
              y={textY}
              textAnchor={textAnchor}
              fill={color}
              fontSize={fontSize}
              fontWeight={600}
            >
              {label}
            </text>
          </g>
        );
      })}
      <circle cx={x} cy={y + 3} r={2} fill={visibleItems[visibleItems.length - 1] ? (MILESTONE_COLORS[visibleItems[visibleItems.length - 1].m.kind] ?? '#475569') : '#475569'} fillOpacity={0.5} />
    </g>
  );
}

function MilestoneReferenceLines({
  milestones,
  domainStart,
  domainEnd,
  highlightAge,
}: {
  milestones: GrowthTimelineResult['milestones'];
  domainStart: number;
  domainEnd: number;
  highlightAge: number;
}) {
  const filtered = useMemo(() => chartMilestones(milestones), [milestones]);
  const grouped = useMemo(() => groupMilestonesByAge(filtered), [filtered]);

  return (
    <>
      {grouped.map(([age, items]) => {
        const isHighlight = age === highlightAge;
        const color = MILESTONE_COLORS[items[items.length - 1]?.kind] ?? '#64748B';
        return (
          <ReferenceLine
            key={age}
            x={age}
            stroke={color}
            strokeOpacity={isHighlight ? 0.55 : 0.15}
            strokeDasharray="3 4"
            strokeWidth={isHighlight ? 1.5 : 1}
            ifOverflow="hidden"
            label={
              isHighlight ? (
                <MilestoneChartLabel
                  items={items}
                  fullLabel
                  textAnchor={milestoneLabelAnchor(age, domainStart, domainEnd)}
                />
              ) : undefined
            }
          />
        );
      })}
    </>
  );
}

function PhaseYearBadges({ timeline }: { timeline: GrowthTimelineResult }) {
  const span = Math.max(timeline.endAge - timeline.currentAge, 1);
  const accFraction = (timeline.retirementAge - timeline.currentAge) / span;
  const ddFraction = (timeline.endAge - timeline.retirementAge) / span;

  return (
    <div className="lgc-timeline-phase-badges" style={{ paddingLeft: 48, paddingRight: 8 }} aria-hidden>
      {timeline.yearsToRetire > 0 && (
        <span
          className="lgc-timeline-year-badge lgc-timeline-year-badge--accumulation"
          style={{ left: `${accFraction * 50}%` }}
        >
          {timeline.yearsToRetire} yr{timeline.yearsToRetire === 1 ? '' : 's'}
        </span>
      )}
      {timeline.yearsInRetirement > 0 && (
        <span
          className="lgc-timeline-year-badge lgc-timeline-year-badge--drawdown"
          style={{ left: `${accFraction * 100 + ddFraction * 50}%` }}
        >
          {timeline.yearsInRetirement} yr{timeline.yearsInRetirement === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

function clampAge(timeline: GrowthTimelineResult, age: number): number {
  return Math.min(timeline.endAge, Math.max(timeline.currentAge, Math.round(age)));
}

function ScrubberSummary({
  timeline,
  point,
  displayAge,
  pinnedAge,
  onUnpin,
  onPinCurrent,
  compact = false,
}: {
  timeline: GrowthTimelineResult;
  point: TimelineYearPoint;
  displayAge: number;
  pinnedAge: number | null;
  onUnpin: () => void;
  onPinCurrent: () => void;
  compact?: boolean;
}) {
  const calendarYear = calendarYearFromPlanTimeline(timeline, displayAge);
  const lifeEvents = milestonesAtAge(timeline.milestones, displayAge);
  const incomePhase = incomePhaseLabelAtAge(
    { phases: timeline.incomePhases },
    displayAge,
    timeline.retirementAge,
  );
  const isDrawdown = point.phase === 'drawdown';
  const saveMo = point.requiredSaveMonthly ?? 0;
  const spendYr = point.requiredSpendAnnual ?? 0;
  const incomeYr = point.takeHomeAnnual ?? point.grossIncome ?? 0;
  const surplusYr = point.surplusAnnual ?? 0;
  const gapToGoal = Math.max(0, timeline.corpusNeeded - point.totalOnTrack);

  return (
    <div className={`lgc-timeline-hero-summary${compact ? ' lgc-timeline-hero-summary--compact' : ''}`}>
      <div className="lgc-timeline-hero-summary-head">
        <div className="lgc-timeline-hero-summary-age">
          <span className="lgc-timeline-hero-age-label">Age {displayAge}</span>
          <span className="lgc-timeline-hero-year-badge">{calendarYear}</span>
          <span
            className={`lgc-timeline-hero-income-badge${isDrawdown ? ' lgc-timeline-hero-income-badge--drawdown' : ''}`}
            title="Income phase"
          >
            {incomePhase}
          </span>
          {!compact &&
            lifeEvents.map((m) => (
              <span
                key={m.id}
                className="lgc-timeline-hero-event-badge"
                style={{ borderColor: MILESTONE_COLORS[m.kind], color: MILESTONE_COLORS[m.kind] }}
                title={m.label}
              >
                {m.label}
              </span>
            ))}
        </div>
        {compact && (
          <div className="lgc-timeline-hero-summary-events" aria-live="polite">
            {lifeEvents.map((m) => (
              <span
                key={m.id}
                className="lgc-timeline-hero-event-badge"
                style={{ borderColor: MILESTONE_COLORS[m.kind], color: MILESTONE_COLORS[m.kind] }}
                title={m.label}
              >
                {m.label}
              </span>
            ))}
          </div>
        )}
        <div className="lgc-timeline-hero-pin-controls">
          {compact ? (
            <>
              {pinnedAge != null && (
                <button type="button" className="lgc-timeline-hero-pin-btn" onClick={onUnpin}>
                  <PinOff size={13} aria-hidden />
                  Follow cursor
                </button>
              )}
              <button
                type="button"
                className="lgc-timeline-hero-pin-btn"
                onClick={onPinCurrent}
                title="Jump to current age"
              >
                Today · {timeline.currentAge}
              </button>
            </>
          ) : (
            <>
              {pinnedAge != null ? (
                <button type="button" className="lgc-timeline-hero-pin-btn" onClick={onUnpin}>
                  <PinOff size={13} aria-hidden />
                  Follow cursor
                </button>
              ) : (
                <span className="lgc-timeline-hero-hint">Move mouse to scrub · click to pin</span>
              )}
              <button
                type="button"
                className="lgc-timeline-hero-pin-btn"
                onClick={onPinCurrent}
                title="Jump to current age"
              >
                Today · {timeline.currentAge}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="lgc-timeline-hero-stats">
        {!isDrawdown && (
          <>
            <div className="lgc-timeline-hero-stat">
              <span className="lgc-timeline-hero-stat-label">Required save</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {formatCurrency(saveMo)}/mo
              </span>
              <span className="lgc-timeline-hero-stat-sub">{formatCurrency(saveMo * 12)}/yr</span>
            </div>
            <div className="lgc-timeline-hero-stat">
              <span className="lgc-timeline-hero-stat-label">Required spend</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {formatCurrency(spendYr / 12)}/mo
              </span>
              <span className="lgc-timeline-hero-stat-sub">{formatCurrency(spendYr)}/yr</span>
            </div>
            <div className="lgc-timeline-hero-stat">
              <span className="lgc-timeline-hero-stat-label">Take-home income</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {formatCurrency(incomeYr / 12)}/mo
              </span>
              <span className="lgc-timeline-hero-stat-sub">{formatCurrency(incomeYr)}/yr</span>
            </div>
            <div
              className={`lgc-timeline-hero-stat${surplusYr >= 0 ? ' lgc-timeline-hero-stat--good' : ' lgc-timeline-hero-stat--warn'}`}
            >
              <span className="lgc-timeline-hero-stat-label">Surplus / gap</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {surplusYr >= 0 ? '+' : ''}
                {formatCurrency(surplusYr / 12)}/mo
              </span>
              <span className="lgc-timeline-hero-stat-sub">
                {surplusYr >= 0 ? 'After required save & spend' : 'Shortfall after needs'}
              </span>
            </div>
          </>
        )}
        {isDrawdown && (
          <>
            <div className="lgc-timeline-hero-stat">
              <span className="lgc-timeline-hero-stat-label">Planned withdrawal</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {formatCurrency(spendYr / 12)}/mo
              </span>
              <span className="lgc-timeline-hero-stat-sub">{formatCurrency(spendYr)}/yr</span>
            </div>
            <div className="lgc-timeline-hero-stat">
              <span className="lgc-timeline-hero-stat-label">Planned portfolio</span>
              <span className="lgc-timeline-hero-stat-value lgc-mono">
                {formatCurrency(point.totalOnTrack)}
              </span>
              <span className="lgc-timeline-hero-stat-sub lgc-timeline-hero-stat-sub--spacer">&nbsp;</span>
            </div>
          </>
        )}
      </div>

      {!compact && (
        <details className="lgc-timeline-hero-details">
          <summary>Account balances &amp; details</summary>
          <div className="lgc-timeline-hero-details-grid">
            {ACCOUNT_KEYS.map((key) => (
              <div key={key} className="lgc-timeline-hero-details-row">
                <span>
                  <span
                    className="lgc-timeline-tooltip-swatch"
                    style={{ background: ACCOUNT_CHART_COLORS[key] }}
                  />
                  {ACCOUNT_CHART_LABELS[key]}
                </span>
                <span className="lgc-mono">{formatCurrency(planAccountBalance(point, key))}</span>
              </div>
            ))}
            <div className="lgc-timeline-hero-details-row">
              <span>Gap to {isDrawdown ? 'corpus goal' : 'goal'}</span>
              <span className="lgc-mono">{formatCurrency(gapToGoal)}</span>
            </div>
            {!isDrawdown && point.availableForSavings != null && point.availableForSavings > 0 && (
              <div className="lgc-timeline-hero-details-row">
                <span>Available (actual surplus)</span>
                <span className="lgc-mono">{formatCurrency(point.availableForSavings)}/yr</span>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

const HERO_PLOT_HEIGHT_FULL = 230;
const HERO_PLOT_HEIGHT_DEFAULT = 253;

function GrowthChart({
  timeline,
  plotHeight = HERO_PLOT_HEIGHT_DEFAULT,
  showPhaseBadges = true,
  hiddenAccounts,
  displayAge,
  followCursor,
  onScrubAge,
  onPinAge,
  onMouseLeaveChart,
}: {
  timeline: GrowthTimelineResult;
  plotHeight?: number;
  showPhaseBadges?: boolean;
  hiddenAccounts: Set<AccountKey>;
  displayAge: number;
  followCursor: boolean;
  onScrubAge: (age: number) => void;
  onPinAge: (age: number) => void;
  onMouseLeaveChart: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);

  const chartTopMargin = useMemo(
    () => chartTopMarginForTimeline(timeline.milestones),
    [timeline.milestones],
  );
  const chartHeight = chartTopMargin + plotHeight + HERO_X_AXIS_HEIGHT;

  const handleMouseMove = useCallback(
    (state: { activeLabel?: string | number }) => {
      if (!followCursor || state?.activeLabel == null) return;
      onScrubAge(clampAge(timeline, Number(state.activeLabel)));
    },
    [followCursor, onScrubAge, timeline],
  );

  const handleClick = useCallback(
    (state: { activeLabel?: string | number }) => {
      if (state?.activeLabel == null) return;
      onPinAge(clampAge(timeline, Number(state.activeLabel)));
      wrapRef.current?.querySelector<HTMLElement>('.recharts-wrapper')?.blur();
    },
    [onPinAge, timeline],
  );

  return (
    <div
      ref={wrapRef}
      className="lgc-timeline-chart-wrap lgc-timeline-chart-wrap--hero"
      style={{ height: chartHeight }}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {showPhaseBadges && <PhaseYearBadges timeline={timeline} />}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={timeline.points}
          margin={{ top: chartTopMargin, right: 8, left: 0, bottom: 0 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={onMouseLeaveChart}
          onClick={handleClick}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ECEAE5" vertical={false} />
          {timeline.yearsInRetirement > 0 && (
            <ReferenceArea
              x1={timeline.retirementAge}
              x2={timeline.endAge}
              strokeOpacity={0}
              fill="#6B6960"
              fillOpacity={0.07}
              ifOverflow="hidden"
            />
          )}
          <XAxis
            dataKey="age"
            type="number"
            domain={[timeline.currentAge, timeline.endAge]}
            tickCount={10}
            tickFormatter={(v) => `${v}`}
            height={HERO_X_AXIS_HEIGHT}
            tickSize={0}
            tickMargin={5}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: 'var(--color-text-secondary)' }}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            width={48}
            tickFormatter={formatAxisMoney}
            axisLine={false}
            tickLine={false}
          />

          {timeline.milestones.length > 0 && (
            <MilestoneReferenceLines
              milestones={timeline.milestones}
              domainStart={timeline.currentAge}
              domainEnd={timeline.endAge}
              highlightAge={displayAge}
            />
          )}

          {PLAN_AREA_KEYS.map(({ key, dataKey }) =>
            hiddenAccounts.has(key) ? null : (
              <Area
                key={key}
                type="monotone"
                dataKey={dataKey}
                name={ACCOUNT_CHART_LABELS[key]}
                stackId="plan"
                stroke={ACCOUNT_CHART_COLORS[key]}
                strokeWidth={1}
                fill={ACCOUNT_CHART_COLORS[key]}
                fillOpacity={0.92}
              />
            ),
          )}

          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
            content={
              <GrowthPlanTooltip timeline={timeline} hiddenAccounts={hiddenAccounts} />
            }
          />

          <ReferenceLine x={displayAge} stroke="var(--pm-accent)" strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScrubberRail({
  timeline,
  displayAge,
  onSelectAge,
}: {
  timeline: GrowthTimelineResult;
  displayAge: number;
  onSelectAge: (age: number) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const markerMap = useMemo(() => buildStripMarkerMap(timeline), [timeline]);

  useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-age="${displayAge}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [displayAge]);

  return (
    <div className="lgc-timeline-strip-wrap">
      <div className="lgc-timeline-strip lgc-timeline-hero-strip" ref={stripRef}>
        {timeline.points.map((p) => {
          const markers = markerMap.get(p.age) ?? [];
          const calendarYear = calendarYearFromPlanTimeline(timeline, p.age);
          const hasLifeEvent = markers.some((m) => m.kind !== 'incomePhase');
          const hasIncomeChange = markers.some((m) => m.kind === 'incomePhase');
          const title = markers.length > 0 ? markers.map((m) => m.label).join(' · ') : undefined;
          const markerIconSize = markers.length > 1 ? 9 : 11;
          const cardMinWidth =
            markers.length <= 1 ? 44 : Math.max(44, 8 + markers.length * 12 + (markers.length - 1) * 2);

          return (
            <button
              key={p.age}
              type="button"
              data-age={p.age}
              className={`lgc-timeline-strip-card${p.age === displayAge ? ' active' : ''}${hasLifeEvent ? ' milestone' : ''}${hasIncomeChange ? ' income-change' : ''}`}
              style={{ minWidth: cardMinWidth }}
              onClick={() => onSelectAge(p.age)}
              title={title}
            >
              <span className="lgc-timeline-strip-markers" aria-hidden={markers.length === 0}>
                {markers.map((m) => (
                  <span
                    key={m.id}
                    className="lgc-timeline-strip-marker"
                    style={{ color: STRIP_MARKER_COLORS[m.kind] ?? '#64748B' }}
                  >
                    <StripMarkerIcon kind={m.kind} size={markerIconSize} />
                  </span>
                ))}
              </span>
              <span className="lgc-timeline-strip-year-badge">{calendarYear}</span>
              <span className="lgc-timeline-strip-age">{p.age}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RequiredIncomeTooltip({
  active,
  payload,
  timeline,
  hiddenCategories,
  coordinate,
  viewBox,
}: {
  active?: boolean;
  payload?: Array<{ payload?: RequiredIncomePoint }>;
  timeline: GrowthTimelineResult;
  hiddenCategories: Set<RequiredCategoryKey>;
  coordinate?: { x: number; y: number };
  viewBox?: { width?: number; height?: number };
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row || row.grossRequiredNominal <= 0) return null;

  const calendarYear = calendarYearFromPlanTimeline(timeline, row.age);

  const requiredRows = REQUIRED_CATEGORIES.filter((c) => !c.allocation)
    .map((cat) => ({
      key: cat.key,
      label: cat.label,
      hint: cat.hint,
      color: cat.color,
      today: row[`${cat.key}` as keyof RequiredIncomePoint] as number,
      future: row[`${cat.key}Nominal` as keyof RequiredIncomePoint] as number,
    }))
    .filter(
      (r) => !hiddenCategories.has(r.key as RequiredCategoryKey) && (r.today > 0 || r.future > 0),
    );

  const allocationRows = REQUIRED_CATEGORIES.filter((c) => c.allocation)
    .map((cat) => ({
      key: cat.key,
      label: cat.label,
      hint: cat.hint,
      color: cat.color,
      today: row[`${cat.key}` as keyof RequiredIncomePoint] as number,
      future: row[`${cat.key}Nominal` as keyof RequiredIncomePoint] as number,
    }))
    .filter(
      (r) => !hiddenCategories.has(r.key as RequiredCategoryKey) && (r.today > 0 || r.future > 0),
    );

  const hasAllocation = allocationRows.length > 0;

  const retireContribRows = [
    { key: 'k401', label: ACCOUNT_CHART_LABELS.k401, today: row.retireContribK401, future: row.retireContribK401Nominal, lumpToday: row.surplusLumpK401, lumpFuture: row.surplusLumpK401Nominal },
    { key: 'roth', label: ACCOUNT_CHART_LABELS.rothIra, today: row.retireContribRoth, future: row.retireContribRothNominal, lumpToday: row.surplusLumpRoth, lumpFuture: row.surplusLumpRothNominal },
    { key: 'hsa', label: ACCOUNT_CHART_LABELS.hsa, today: row.retireContribHsa, future: row.retireContribHsaNominal, lumpToday: row.surplusLumpHsa, lumpFuture: row.surplusLumpHsaNominal },
    {
      key: 'taxable',
      label: ACCOUNT_CHART_LABELS.taxable,
      today: row.retireContribTaxable,
      future: row.retireContribTaxableNominal,
      lumpToday: row.surplusLumpTaxable,
      lumpFuture: row.surplusLumpTaxableNominal,
    },
  ];

  const retireContribTotalToday = retireContribRows.reduce((s, r) => s + r.today + r.lumpToday, 0);
  const retireContribTotalFuture = retireContribRows.reduce((s, r) => s + r.future + r.lumpFuture, 0);
  const showRetireContribTable =
    retireContribTotalFuture > 0 || row.retirementContribNominal > 0 || row.surplusToRetirementNominal > 0;

  return (
    <div
      className="lgc-required-income-tooltip"
      role="tooltip"
      style={chartTooltipFlipStyle(coordinate, viewBox)}
    >
      <p className="lgc-required-income-tooltip__head">
        Age {row.age} · {calendarYear}
      </p>

      <div className="lgc-required-income-tooltip__section">
        <p className="lgc-required-income-tooltip__section-title">Required needs</p>
        <table className="lgc-required-income-tooltip__table">
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Today&apos;s $</th>
              <th scope="col">Future $</th>
            </tr>
          </thead>
          <tbody>
            {requiredRows.map((r) => (
              <tr key={r.key}>
                <td>
                  <span
                    className="lgc-required-income-tooltip__swatch"
                    style={{ backgroundColor: r.color }}
                    aria-hidden
                  />
                  <span className="lgc-required-income-tooltip__label">
                    {r.label}
                    {r.hint && (
                      <span className="lgc-required-income-tooltip__label-hint">{r.hint}</span>
                    )}
                  </span>
                </td>
                <td className="lgc-mono">{formatCurrency(r.today)}/yr</td>
                <td className="lgc-mono">{formatCurrency(r.future)}/yr</td>
              </tr>
            ))}
            <tr className="lgc-required-income-tooltip__row--total">
              <td>Required gross</td>
              <td className="lgc-mono">{formatCurrency(row.grossRequired)}/yr</td>
              <td className="lgc-mono">{formatCurrency(row.grossRequiredNominal)}/yr</td>
            </tr>
          </tbody>
        </table>
      </div>

      {hasAllocation && (
        <div className="lgc-required-income-tooltip__section">
          <p className="lgc-required-income-tooltip__section-title">
            Surplus allocation
            <span className="lgc-required-income-tooltip__section-hint">
              fills gap to planned income
            </span>
          </p>
          <table className="lgc-required-income-tooltip__table">
            <tbody>
              {allocationRows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span
                      className="lgc-required-income-tooltip__swatch"
                      style={{ backgroundColor: r.color }}
                      aria-hidden
                    />
                    <span className="lgc-required-income-tooltip__label">
                      {r.label}
                      {r.hint && (
                        <span className="lgc-required-income-tooltip__label-hint">{r.hint}</span>
                      )}
                    </span>
                  </td>
                  <td className="lgc-mono">{formatCurrency(r.today)}/yr</td>
                  <td className="lgc-mono">{formatCurrency(r.future)}/yr</td>
                </tr>
              ))}
            </tbody>
          </table>
          {showRetireContribTable && (
            <>
              <p className="lgc-required-income-tooltip__subsection-title">
                Goal-path contributions
                <span className="lgc-required-income-tooltip__section-hint">
                  configured accounts + on-pace extra (take-home); $0 = none routed this year
                </span>
              </p>
              <table className="lgc-required-income-tooltip__table">
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Today&apos;s $</th>
                    <th scope="col">Future $</th>
                  </tr>
                </thead>
                <tbody>
                  {retireContribRows.map((r) => {
                    const todayTotal = r.today + r.lumpToday;
                    const futureTotal = r.future + r.lumpFuture;
                    const isZero = todayTotal <= 0 && futureTotal <= 0;
                    return (
                      <tr
                        key={r.key}
                        className={`lgc-required-income-tooltip__row--detail${isZero ? ' lgc-required-income-tooltip__row--zero' : ''}`}
                      >
                        <td>{r.label}</td>
                        <td className="lgc-mono">
                          {isZero ? '—' : `${formatCurrency(todayTotal)}/yr`}
                        </td>
                        <td className="lgc-mono">
                          {isZero ? '—' : `${formatCurrency(futureTotal)}/yr`}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="lgc-required-income-tooltip__row--subtotal">
                    <td>Total to accounts</td>
                    <td className="lgc-mono">{formatCurrency(retireContribTotalToday)}/yr</td>
                    <td className="lgc-mono">{formatCurrency(retireContribTotalFuture)}/yr</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {row.surplusAnnualNominal > 0 && (
            <dl className="lgc-required-income-tooltip__meta">
              <dt>Take-home surplus</dt>
              <dd className="lgc-mono">{formatCurrency(row.surplusAnnual)}/yr</dd>
            </dl>
          )}
        </div>
      )}

      <div className="lgc-required-income-tooltip__footer">
        <div className="lgc-required-income-tooltip__footer-row">
          <span>Planned income</span>
          <span className="lgc-mono">{formatCurrency(row.income)}/yr</span>
        </div>
        {row.incomeGapNominal > 0 && (
          <div className="lgc-required-income-tooltip__footer-row lgc-required-income-tooltip__footer-row--muted">
            <span>Income gap</span>
            <span className="lgc-mono">{formatCurrency(row.incomeGap)}/yr</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RequiredIncomeChart({
  timeline,
  plotHeight = HERO_PLOT_HEIGHT_DEFAULT,
  showPhaseBadges = true,
  displayAge,
  followCursor,
  onScrubAge,
  onPinAge,
  onMouseLeaveChart,
  hiddenCategories,
}: {
  timeline: GrowthTimelineResult;
  plotHeight?: number;
  showPhaseBadges?: boolean;
  displayAge: number;
  followCursor: boolean;
  onScrubAge: (age: number) => void;
  onPinAge: (age: number) => void;
  onMouseLeaveChart: () => void;
  hiddenCategories: Set<RequiredCategoryKey>;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);

  const chartTopMargin = useMemo(
    () => chartTopMarginForTimeline(timeline.milestones),
    [timeline.milestones],
  );
  const chartHeight = chartTopMargin + plotHeight + HERO_X_AXIS_HEIGHT;

  const data = useMemo(() => buildRequiredIncomeData(timeline), [timeline]);

  const handleMouseMove = useCallback(
    (state: { activeLabel?: string | number }) => {
      if (!followCursor || state?.activeLabel == null) return;
      onScrubAge(clampAge(timeline, Number(state.activeLabel)));
    },
    [followCursor, onScrubAge, timeline],
  );

  const handleClick = useCallback(
    (state: { activeLabel?: string | number }) => {
      if (state?.activeLabel == null) return;
      onPinAge(clampAge(timeline, Number(state.activeLabel)));
      wrapRef.current?.querySelector<HTMLElement>('.recharts-wrapper')?.blur();
    },
    [onPinAge, timeline],
  );

  return (
    <div
      ref={wrapRef}
      className="lgc-timeline-chart-wrap lgc-timeline-chart-wrap--hero"
      style={{ height: chartHeight }}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {showPhaseBadges && <PhaseYearBadges timeline={timeline} />}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={data}
          margin={{ top: chartTopMargin, right: 8, left: 0, bottom: 0 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={onMouseLeaveChart}
          onClick={handleClick}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ECEAE5" vertical={false} />
          {timeline.yearsInRetirement > 0 && (
            <ReferenceArea
              x1={timeline.retirementAge}
              x2={timeline.endAge}
              strokeOpacity={0}
              fill="#6B6960"
              fillOpacity={0.07}
              ifOverflow="hidden"
            />
          )}
          <XAxis
            dataKey="age"
            type="number"
            domain={[timeline.currentAge, timeline.endAge]}
            tickCount={10}
            tickFormatter={(v) => `${v}`}
            height={HERO_X_AXIS_HEIGHT}
            tickSize={0}
            tickMargin={5}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: 'var(--color-text-secondary)' }}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            width={48}
            tickFormatter={formatAxisMoney}
            axisLine={false}
            tickLine={false}
          />

          {timeline.milestones.length > 0 && (
            <MilestoneReferenceLines
              milestones={timeline.milestones}
              domainStart={timeline.currentAge}
              domainEnd={timeline.endAge}
              highlightAge={displayAge}
            />
          )}

          {REQUIRED_CATEGORIES.map((cat) =>
            hiddenCategories.has(cat.key) ? null : (
              <Bar
                key={cat.key}
                dataKey={cat.key}
                name={cat.label}
                stackId="required"
                fill={cat.color}
                isAnimationActive={false}
              />
            ),
          )}

          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
            content={
              <RequiredIncomeTooltip timeline={timeline} hiddenCategories={hiddenCategories} />
            }
          />

          <Line
            type="monotone"
            dataKey="income"
            name="Planned income"
            stroke={REQUIRED_INCOME_LINE_COLOR}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
          />

          <ReferenceLine x={displayAge} stroke="var(--pm-accent)" strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function RequiredIncomeLegend({
  hiddenCategories,
  onToggle,
}: {
  hiddenCategories: Set<RequiredCategoryKey>;
  onToggle: (key: RequiredCategoryKey) => void;
}) {
  const requiredCats = REQUIRED_CATEGORIES.filter((c) => !c.allocation);
  const allocationCats = REQUIRED_CATEGORIES.filter((c) => c.allocation);

  return (
    <div className="lgc-timeline-legend lgc-timeline-hero-legend">
      {requiredCats.map((cat) => (
        <button
          key={cat.key}
          type="button"
          className={`lgc-timeline-legend-item${hiddenCategories.has(cat.key) ? ' off' : ''}`}
          onClick={() => onToggle(cat.key)}
          title={cat.hint}
        >
          <span className="lgc-timeline-legend-swatch" style={{ background: cat.color }} />
          {cat.label}
        </button>
      ))}
      <span className="lgc-timeline-legend-item lgc-timeline-legend-item--static">
        <span
          className="lgc-timeline-legend-swatch lgc-timeline-legend-swatch--dashed"
          style={{ borderColor: REQUIRED_INCOME_LINE_COLOR }}
        />
        Planned income
      </span>
      {allocationCats.length > 0 && (
        <span className="lgc-timeline-legend-divider" aria-hidden />
      )}
      {allocationCats.map((cat) => (
        <button
          key={cat.key}
          type="button"
          className={`lgc-timeline-legend-item lgc-timeline-legend-item--allocation${hiddenCategories.has(cat.key) ? ' off' : ''}`}
          onClick={() => onToggle(cat.key)}
        >
          <span className="lgc-timeline-legend-swatch" style={{ background: cat.color }} />
          {cat.label}
        </button>
      ))}
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: RequiredIncomeView;
  onChange: (next: RequiredIncomeView) => void;
}) {
  return (
    <div className="lgc-timeline-view-toggle" role="tablist" aria-label="Timeline view">
      <button
        type="button"
        role="tab"
        aria-selected={view === 'growth'}
        className={`lgc-timeline-view-toggle-btn${view === 'growth' ? ' active' : ''}`}
        onClick={() => onChange('growth')}
      >
        Growth
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={view === 'required'}
        className={`lgc-timeline-view-toggle-btn${view === 'required' ? ' active' : ''}`}
        onClick={() => onChange('required')}
      >
        Required income
      </button>
    </div>
  );
}

function AccountLegend({
  hiddenAccounts,
  onToggle,
}: {
  hiddenAccounts: Set<AccountKey>;
  onToggle: (key: AccountKey) => void;
}) {
  return (
    <div className="lgc-timeline-legend lgc-timeline-hero-legend">
      {ACCOUNT_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          className={`lgc-timeline-legend-item${hiddenAccounts.has(key) ? ' off' : ''}`}
          onClick={() => onToggle(key)}
        >
          <span className="lgc-timeline-legend-swatch" style={{ background: ACCOUNT_CHART_COLORS[key] }} />
          {ACCOUNT_CHART_LABELS[key]}
        </button>
      ))}
    </div>
  );
}

const LgcTimelineHero: React.FC<LgcTimelineHeroProps> = ({
  timeline,
  fullPage = false,
  onPinEdit,
}) => {
  const [scrubAge, setScrubAge] = useState(timeline.currentAge);
  const [pinnedAge, setPinnedAge] = useState<number | null>(null);
  const [hiddenAccounts, setHiddenAccounts] = useState<Set<AccountKey>>(() => new Set());
  const [hiddenCategories, setHiddenCategories] = useState<Set<RequiredCategoryKey>>(() => new Set());
  const [view, setView] = useState<RequiredIncomeView>('growth');

  const displayAge = pinnedAge ?? scrubAge;
  const followCursor = pinnedAge === null;

  useEffect(() => {
    setScrubAge(timeline.currentAge);
    setPinnedAge(null);
  }, [timeline.currentAge, timeline.retirementAge, timeline.endAge]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPinnedAge(null);
        setScrubAge(timeline.currentAge);
        return;
      }
      if (pinnedAge == null) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setPinnedAge(clampAge(timeline, pinnedAge - 1));
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setPinnedAge(clampAge(timeline, pinnedAge + 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinnedAge, timeline]);

  const displayPoint =
    timeline.points.find((p) => p.age === displayAge) ?? timeline.points[timeline.points.length - 1];

  const toggleAccount = (key: AccountKey) => {
    setHiddenAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCategory = (key: RequiredCategoryKey) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section
      className={`lgc-timeline-hero${fullPage ? ' lgc-timeline-hero--full-page' : ''}`}
      aria-label="Growth timeline"
    >
      <div className="lgc-timeline-hero-header">
        <div className="lgc-timeline-hero-header-title">
          <h2 className="lgc-timeline-hero-title">
            {view === 'growth' ? 'Growth timeline' : 'Required income by year'}
          </h2>
          <p className="lgc-timeline-hero-sub">
            {view === 'growth' ? (
              <>
                Age {timeline.currentAge} → {timeline.endAge} · Retire {timeline.retirementAge} · Goal{' '}
                {formatCurrency(timeline.corpusNeeded)}
              </>
            ) : (
              <>Required gross by age · today&apos;s $</>
            )}
          </p>
        </div>
        <div className="lgc-timeline-hero-header-actions">
          <ViewToggle view={view} onChange={setView} />
          {view === 'growth' ? (
            <AccountLegend hiddenAccounts={hiddenAccounts} onToggle={toggleAccount} />
          ) : (
            <RequiredIncomeLegend
              hiddenCategories={hiddenCategories}
              onToggle={toggleCategory}
            />
          )}
        </div>
      </div>

      <div className={`lgc-timeline-hero-body${fullPage ? ' lgc-timeline-hero-body--stacked' : ''}`}>
        {fullPage ? (
          <ScrubberSummary
            timeline={timeline}
            point={displayPoint}
            displayAge={displayAge}
            pinnedAge={pinnedAge}
            compact
            onUnpin={() => {
              setPinnedAge(null);
              setScrubAge(timeline.currentAge);
            }}
            onPinCurrent={() => {
              setPinnedAge(timeline.currentAge);
              setScrubAge(timeline.currentAge);
              onPinEdit?.(timeline.currentAge);
            }}
          />
        ) : (
          <aside className="lgc-timeline-hero-panel">
            <ScrubberSummary
              timeline={timeline}
              point={displayPoint}
              displayAge={displayAge}
              pinnedAge={pinnedAge}
              onUnpin={() => {
                setPinnedAge(null);
                setScrubAge(timeline.currentAge);
              }}
              onPinCurrent={() => {
                setPinnedAge(timeline.currentAge);
                setScrubAge(timeline.currentAge);
                onPinEdit?.(timeline.currentAge);
              }}
            />
          </aside>
        )}

        <div className="lgc-timeline-hero-chart-col">
          {view === 'growth' ? (
            <GrowthChart
              timeline={timeline}
              plotHeight={fullPage ? HERO_PLOT_HEIGHT_FULL : HERO_PLOT_HEIGHT_DEFAULT}
              showPhaseBadges={!fullPage}
              hiddenAccounts={hiddenAccounts}
              displayAge={displayAge}
              followCursor={followCursor}
              onScrubAge={setScrubAge}
              onPinAge={(age) => {
                setPinnedAge(age);
                onPinEdit?.(age);
              }}
              onMouseLeaveChart={() => {
                if (pinnedAge === null) setScrubAge(timeline.currentAge);
              }}
            />
          ) : (
            <RequiredIncomeChart
              timeline={timeline}
              plotHeight={fullPage ? HERO_PLOT_HEIGHT_FULL : HERO_PLOT_HEIGHT_DEFAULT}
              showPhaseBadges={!fullPage}
              hiddenCategories={hiddenCategories}
              displayAge={displayAge}
              followCursor={followCursor}
              onScrubAge={setScrubAge}
              onPinAge={(age) => {
                setPinnedAge(age);
                onPinEdit?.(age);
              }}
              onMouseLeaveChart={() => {
                if (pinnedAge === null) setScrubAge(timeline.currentAge);
              }}
            />
          )}
          <ScrubberRail
            timeline={timeline}
            displayAge={displayAge}
            onSelectAge={(age) => {
              setPinnedAge(age);
              setScrubAge(age);
              onPinEdit?.(age);
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default LgcTimelineHero;
